import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { getJob, applyJob } from "../services/api";
import { useAuth } from "../context/AuthContext";

export default function JobDetail() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState("description");
  const [applied, setApplied]       = useState(false);
  const [showModal, setShowModal]   = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [resume, setResume]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState('');
  const [name, setName]   = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setPhone(user.phone || '');
    }
  }, [user]);

  useEffect(() => {
    setLoading(true);
    getJob(id)
      .then((data) => setJob(data.job || data))
      .catch(() => setError("Failed to load job details."))
      .finally(() => setLoading(false));
  }, [id]);

  const handleApply = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let resumeUrl = '';
      if (resumeFile) {
        setUploadProgress('Uploading resume...');
        const formData = new FormData();
        formData.append('resume', resumeFile);
        const res = await fetch('http://localhost:5001/api/upload/resume', {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: formData,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Resume upload failed');
        resumeUrl = data.url;
      }
      setUploadProgress('Submitting application...');
      await applyJob(id, { name, email, phone, coverLetter, resume: resumeUrl });
      setApplied(true);
      setShowModal(false);
      navigate('/my-applications');
    } catch (err) {
      alert(err.message || 'Failed to apply.');
    } finally {
      setSubmitting(false);
      setUploadProgress('');
    }
  };

  if (loading)
    return (
      <div className="text-center py-5">
        <i className="fa fa-spinner fa-spin fa-2x text-primary"></i>
        <p className="text-muted mt-3">Loading job...</p>
      </div>
    );

  if (error || !job)
    return (
      <div className="text-center py-5">
        <p className="text-danger">{error || "Job not found."}</p>
      </div>
    );

  return (
    <>
      <PageHeader title="Job Detail" breadcrumb="Job Detail" />

      <section className="section-pad">
        <div className="container">
          <div className="jd-layout">
            {/* ── Main Content ── */}
            <div className="jd-main">
              {/* Job Hero Card */}
              <div className="jd-hero-card">
                <div className="jd-hero-top">
                  <div className="jd-hero-left">
                    <div className="jd-company-logo">
                      {job.logo ? (
                        <img src={job.logo} alt="" />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            background: "#e8f0ff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#0066FF",
                            fontSize: 28,
                          }}
                        >
                          <i className="fa fa-building"></i>
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="jd-title-row">
                        <h2>{job.title}</h2>
                        {job.tag && (
                          <span
                            className={`jl-tag tag-${job.tag?.toLowerCase()}`}
                          >
                            {job.tag}
                          </span>
                        )}
                      </div>
                      <p className="jd-company-name">
                        <i className="fa fa-building me-2 text-primary"></i>
                        {job.company}
                      </p>
                      <div className="jd-hero-meta">
                        <span>
                          <i className="fa fa-map-marker-alt"></i>
                          {job.location}
                        </span>
                        <span>
                          <i className="far fa-clock"></i>
                          {job.type}
                        </span>
                        <span>
                          <i className="far fa-money-bill-alt"></i>
                          {job.salary || "Negotiable"}
                        </span>
                        <span>
                          <i className="far fa-calendar-alt"></i>
                          {job.createdAt
                            ? new Date(job.createdAt).toLocaleDateString()
                            : ""}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="jd-quick-stats">
                  {[
                    {
                      icon: "fa-briefcase",
                      color: "#0066FF",
                      bg: "#e8f0ff",
                      label: "Job Type",
                      value: job.type,
                    },
                    {
                      icon: "fa-layer-group",
                      color: "#00C896",
                      bg: "#e6fff6",
                      label: "Category",
                      value: job.category,
                    },
                    {
                      icon: "fa-chart-bar",
                      color: "#f59e0b",
                      bg: "#fff8e6",
                      label: "Experience",
                      value: job.experience,
                    },
                    {
                      icon: "fa-users",
                      color: "#8b5cf6",
                      bg: "#f3eeff",
                      label: "Vacancies",
                      value: job.vacancies
                        ? `${job.vacancies} Positions`
                        : "N/A",
                    },
                    {
                      icon: "fa-calendar",
                      color: "#ef4444",
                      bg: "#fff0f0",
                      label: "Deadline",
                      value: job.deadline
                        ? new Date(job.deadline).toLocaleDateString()
                        : "N/A",
                    },
                  ].map((s, i) => (
                    <div className="jd-stat-item" key={i}>
                      <div
                        className="jd-stat-icon"
                        style={{ background: s.bg, color: s.color }}
                      >
                        <i className={`fa ${s.icon}`}></i>
                      </div>
                      <div>
                        <span className="jd-stat-label">{s.label}</span>
                        <strong className="jd-stat-value">{s.value}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tabs */}
              <div className="jd-tabs">
                {["description", "requirements", "benefits"].map((t) => (
                  <button
                    key={t}
                    className={`jd-tab ${activeTab === t ? "active" : ""}`}
                    onClick={() => setActiveTab(t)}
                  >
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="jd-content-card">
                {activeTab === "description" && (
                  <>
                    <h4>Job Description</h4>
                    <p>{job.description || "No description provided."}</p>
                    {job.responsibilities?.length > 0 && (
                      <>
                        <h4 className="mt-4">Responsibilities</h4>
                        <ul className="jd-list">
                          {job.responsibilities.map((r, i) => (
                            <li key={i}>
                              <i className="fa fa-check-circle"></i>
                              {r}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    {job.skills?.length > 0 && (
                      <>
                        <h4 className="mt-4">Required Skills</h4>
                        <div className="jd-skills">
                          {job.skills.map((s, i) => (
                            <span key={i} className="jd-skill-tag">
                              {s}
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
                {activeTab === "requirements" && (
                  <>
                    <h4>Requirements</h4>
                    {job.requirements?.length > 0 ? (
                      <ul className="jd-list">
                        {job.requirements.map((r, i) => (
                          <li key={i}>
                            <i className="fa fa-check-circle"></i>
                            {r}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>No requirements listed.</p>
                    )}
                  </>
                )}
                {activeTab === "benefits" && (
                  <>
                    <h4>What We Offer</h4>
                    {job.benefits?.length > 0 ? (
                      <ul className="jd-list">
                        {job.benefits.map((b, i) => (
                          <li key={i}>
                            <i className="fa fa-check-circle"></i>
                            {b}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>No benefits listed.</p>
                    )}
                  </>
                )}
              </div>

              <div className="jd-apply-cta">
                <button
                  className={`jd-save-btn ${saved ? "saved" : ""}`}
                  onClick={() => setSaved(!saved)}
                >
                  <i className={`${saved ? "fa" : "far"} fa-heart me-2`}></i>
                  {saved ? "Saved" : "Save Job"}
                </button>
                <button
                  className="jd-apply-btn"
                  onClick={() => setShowModal(true)}
                  disabled={applied}
                >
                  <i className="fa fa-paper-plane me-2"></i>
                  {applied ? 'Applied!' : 'Apply Now'}
                </button>
              </div>

              {/* Related Jobs */}
              <div className="jd-related">
                <h4 className="mb-4">Similar Jobs</h4>
              </div>
            </div>

            {/* ── Sidebar ── */}
            <aside className="jd-sidebar">
              {/* Company Card */}
              <div className="jd-sidebar-card">
                <div className="jd-company-header">
                  <div
                    style={{
                      width: 50,
                      height: 50,
                      background: "#e8f0ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#0066FF",
                      borderRadius: 8,
                    }}
                  >
                    <i className="fa fa-building"></i>
                  </div>
                  <div>
                    <h6>{job.company}</h6>
                    <span>{job.category}</span>
                  </div>
                </div>
                <div className="jd-company-stats">
                  {[
                    {
                      icon: "fa-map-marker-alt",
                      label: "Location:  ",
                      value: job.location,
                    },
                  ].map((s, i) => (
                    <div className="jd-company-stat" key={i}>
                      <i className={`fa ${s.icon}`}></i>
                      <div>
                        <span>{s.label}</span>
                        <strong>{s.value}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Job Overview */}
              <div className="jd-sidebar-card">
                <h6 className="jd-sidebar-title">Job Overview</h6>
                {[
                  {
                    icon: "fa-calendar-plus",
                    color: "#0066FF",
                    label: "Date Posted",
                    value: job.createdAt
                      ? new Date(job.createdAt).toLocaleDateString()
                      : "N/A",
                  },
                  {
                    icon: "fa-calendar-times",
                    color: "#ef4444",
                    label: "Deadline",
                    value: job.deadline
                      ? new Date(job.deadline).toLocaleDateString()
                      : "N/A",
                  },
                  {
                    icon: "fa-briefcase",
                    color: "#00C896",
                    label: "Job Type",
                    value: job.type,
                  },
                  {
                    icon: "fa-money-bill-alt",
                    color: "#f59e0b",
                    label: "Salary",
                    value: job.salary || "Negotiable",
                  },
                  {
                    icon: "fa-map-marker-alt",
                    color: "#8b5cf6",
                    label: "Location",
                    value: job.location,
                  },
                  {
                    icon: "fa-layer-group",
                    color: "#06b6d4",
                    label: "Experience",
                    value: job.experience,
                  },
                ].map((o, i) => (
                  <div className="jd-overview-item" key={i}>
                    <div
                      className="jd-overview-icon"
                      style={{ color: o.color, background: o.color + "18" }}
                    >
                      <i className={`fa ${o.icon}`}></i>
                    </div>
                    <div>
                      <span>{o.label}</span>
                      <strong>{o.value}</strong>
                    </div>
                  </div>
                ))}
              </div>

              {/* Share */}
              <div className="jd-sidebar-card">
                <h6 className="jd-sidebar-title">Share This Job</h6>
                <div className="jd-share-btns">
                  {[
                    {
                      icon: "fab fa-linkedin-in",
                      color: "#0077b5",
                      label: "LinkedIn",
                    },
                    {
                      icon: "fab fa-twitter",
                      color: "#1da1f2",
                      label: "Twitter",
                    },
                    {
                      icon: "fab fa-facebook-f",
                      color: "#1877f2",
                      label: "Facebook",
                    },
                    { icon: "fa fa-link", color: "#888", label: "Copy Link" },
                  ].map((s, i) => (
                    <a
                      key={i}
                      href="#"
                      className="jd-share-btn"
                      style={{ "--share-color": s.color }}
                    >
                      <i className={s.icon}></i>
                      <span>{s.label}</span>
                    </a>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Apply Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: '100%', maxWidth: 500, position: 'relative' }}>
            <button onClick={() => setShowModal(false)} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>
              <i className="fa fa-times"></i>
            </button>
            <h4 className="mb-1">Apply for {job.title}</h4>
            <p style={{ color: '#888', fontSize: 13, marginBottom: 20 }}>{job.company}</p>
            <form onSubmit={handleApply}>
              <div style={{ display: 'flex', gap: 12 }} className="mb-3">
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 600, fontSize: 14 }}>Full Name <span style={{ color: '#ef4444' }}>*</span></label>
                  <input required className="form-control mt-1" placeholder="Your full name" value={name} onChange={e => setName(e.target.value)} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 600, fontSize: 14 }}>Phone <span style={{ color: '#ef4444' }}>*</span></label>
                  <input required className="form-control mt-1" placeholder="+91 00000 00000" value={phone} onChange={e => setPhone(e.target.value)} />
                </div>
              </div>
              <div className="mb-3">
                <label style={{ fontWeight: 600, fontSize: 14 }}>Email <span style={{ color: '#ef4444' }}>*</span></label>
                <input required type="email" className="form-control mt-1" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <div className="mb-3">
                <label style={{ fontWeight: 600, fontSize: 14 }}>Cover Letter</label>
                <textarea
                  className="form-control mt-1"
                  rows={4}
                  placeholder="Write a brief cover letter..." 
                  value={coverLetter}
                  onChange={e => setCoverLetter(e.target.value)}
                />
              </div>
              <div className="mb-4">
                <label style={{ fontWeight: 600, fontSize: 14 }}>Upload Resume <span style={{ color: '#aaa', fontWeight: 400 }}>(PDF, DOC, DOCX — max 5MB)</span></label>
                <div
                  style={{ border: '2px dashed #d0d7e3', borderRadius: 8, padding: '20px', textAlign: 'center', marginTop: 6, cursor: 'pointer', background: resumeFile ? '#f0fff8' : '#fafbff', transition: 'all 0.2s' }}
                  onClick={() => document.getElementById('resume-upload').click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setResumeFile(f); }}
                >
                  <input
                    id="resume-upload"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    style={{ display: 'none' }}
                    onChange={e => setResumeFile(e.target.files[0] || null)}
                  />
                  {resumeFile ? (
                    <>
                      <i className="fa fa-file-alt" style={{ fontSize: 24, color: '#00C896' }}></i>
                      <p style={{ margin: '8px 0 0', fontWeight: 600, color: '#00C896', fontSize: 13 }}>{resumeFile.name}</p>
                      <span
                        style={{ fontSize: 12, color: '#888', cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={e => { e.stopPropagation(); setResumeFile(null); }}
                      >Remove</span>
                    </>
                  ) : (
                    <>
                      <i className="fa fa-cloud-upload-alt" style={{ fontSize: 28, color: '#b0bec5' }}></i>
                      <p style={{ margin: '8px 0 0', color: '#888', fontSize: 13 }}>Drag & drop or <span style={{ color: '#0066FF', fontWeight: 600 }}>browse</span></p>
                    </>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: 8, background: '#f5f5f5', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" className="jd-apply-btn" style={{ flex: 1 }} disabled={submitting}>
                  {submitting
                    ? <><i className="fa fa-spinner fa-spin me-2"></i>{uploadProgress || 'Submitting...'}</>
                    : <><i className="fa fa-paper-plane me-2"></i>Submit Application</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
